import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import Paywall from '../components/Paywall'
import LinkAccountForm from '../components/LinkAccountForm'
import { Button, Card, ErrorMessage, Field, Spinner } from '../components/ui'
import { providerLabel } from '../lib/format'

const SUPPORT_EMAIL = 'justin+lolcollusion@soonahn.ca'

// Spelled out rather than numeric. These dates decide whether someone is about
// to be charged, and 9/8 means two different days depending on where you are.
function longDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function describe(entitlement) {
  if (!entitlement) return null

  switch (entitlement.source) {
    case 'comp':
      return 'You have complimentary access, with no expiry and nothing to pay.'
    case 'subscription':
      return `Subscribed. Access runs until ${new Date(entitlement.expires_at).toLocaleDateString()}.`
    default:
      return `${entitlement.free_remaining} of ${entitlement.free_limit} free evaluations left this season.`
  }
}

// Pausing, cancelling, and changing your mind about either.
//
// Deliberately flat: every action available in a given state is a button on
// this card, with no dialog in front of it and nothing offering a discount to
// stay. Cancelling has to be no harder than subscribing was — the renewal law
// requires it and /terms says so in as many words — and a "are you sure?" step
// bolted onto the way out, but not onto the way in, is exactly the asymmetry
// that rule exists to forbid. A stray click is survivable here in a way it is
// not on the disconnect button below: nothing is destroyed, no charge happens,
// and Resume is on screen the moment Cancel lands.
function SubscriptionCard({ subscription }) {
  const queryClient = useQueryClient()

  // Access and billing state are separate reads and both move together here.
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] })
    queryClient.invalidateQueries({ queryKey: ['billing', 'entitlement'] })
  }

  const pause = useMutation({ mutationFn: () => api.pauseSubscription(), onSuccess: refresh })
  const resume = useMutation({ mutationFn: () => api.resumeSubscription(), onSuccess: refresh })
  const cancel = useMutation({ mutationFn: () => api.cancelSubscription(), onSuccess: refresh })
  const portal = useMutation({
    mutationFn: () => api.openPortal(),
    onSuccess: (data) => window.location.assign(data.portal.url),
  })

  const { state, manageable, provider, access_until, next_charge_at, resumes_at, price } =
    subscription

  const busy = pause.isPending || resume.isPending || cancel.isPending

  // Whether the term that was paid for has run out. True for a subscription
  // cancelled or paused during a season off, whose access ended months ago.
  const accessEnded = !access_until || new Date(access_until) <= new Date()

  // A pause whose paid term has already run out: the season off has started.
  // Resuming from here is not an undo — it charges the card and starts a new
  // term today — so it gets a different label and its own warning.
  const seasonOffStarted = state === 'paused' && accessEnded

  // Apple bills iOS purchases and only Apple can change them. Saying where it
  // lives is more use than a button that would fail on the server.
  if (!manageable) {
    return (
      <Card title="Subscription" subtitle={state === 'ended' ? 'Ended.' : 'Managed elsewhere.'}>
        {provider === 'apple' ? (
          <p className="text-sm text-slate-300">
            You subscribed in the iOS app, so Apple bills it and Apple is the only place it can
            be paused or cancelled — open your Apple subscription settings on your device.
            Deleting your lolcollusion account does <strong>not</strong> cancel it.
          </p>
        ) : (
          <p className="text-sm text-slate-300">
            There is nothing to pause or cancel here.
          </p>
        )}
      </Card>
    )
  }

  return (
    <Card title="Subscription">
      {state === 'active' && (
        <p className="text-sm text-slate-300">
          Renews on {longDate(next_charge_at)}
          {price ? ` for ${price}` : ''}. We will email you before that happens.
        </p>
      )}

      {state === 'past_due' && (
        <p className="text-sm text-amber-200">
          We could not take the last payment. Your access continues while Stripe retries — update
          your card below and nothing else changes.
        </p>
      )}

      {state === 'paused' && !seasonOffStarted && (
        <p className="text-sm text-slate-300">
          Paused. Your access runs until {longDate(access_until)} as normal, nothing is charged
          for the season after that, and billing starts again on {longDate(resumes_at)}.
        </p>
      )}

      {state === 'paused' && seasonOffStarted && (
        <p className="text-sm text-slate-300">
          Paused, and your season off has started — that is why you have no access right now.
          Nothing is being charged. Billing starts again on {longDate(resumes_at)}, and your
          access comes back the same day.
        </p>
      )}

      {/* The two are a real distinction, not a copy flourish. Cancelling
          during a season off leaves a subscription whose paid term ended
          months ago, and "you keep access until <a date in the past>" reads
          as a mistake to the one person who most needs to trust it. */}
      {state === 'canceling' && !accessEnded && (
        <p className="text-sm text-slate-300">
          Set to cancel. You keep access until {longDate(access_until)} — you have paid for it —
          and there is no further charge after that.
        </p>
      )}

      {state === 'canceling' && accessEnded && (
        <p className="text-sm text-slate-300">
          Cancelled. The term you had paid for has already ended, so there is nothing further to
          come and nothing further to pay.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {(state === 'active' || state === 'past_due') && (
          <Button variant="secondary" disabled={busy} onClick={() => pause.mutate()}>
            {pause.isPending ? 'Pausing…' : 'Pause for a season'}
          </Button>
        )}

        {(state === 'paused' || state === 'canceling') && (
          <Button variant="secondary" disabled={busy} onClick={() => resume.mutate()}>
            {resume.isPending
              ? 'Resuming…'
              : seasonOffStarted
                ? 'Start again now'
                : state === 'canceling'
                  ? 'Keep my subscription'
                  : 'Resume subscription'}
          </Button>
        )}

        {state !== 'canceling' && (
          <Button variant="secondary" disabled={busy} onClick={() => cancel.mutate()}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel subscription'}
          </Button>
        )}

        <Button variant="secondary" disabled={portal.isPending} onClick={() => portal.mutate()}>
          {portal.isPending ? 'Opening…' : 'Card and invoices'}
        </Button>
      </div>

      {(state === 'active' || state === 'past_due') && (
        <p className="mt-2 text-xs text-slate-500">
          Not playing next season? Pausing skips one year's charge and picks the subscription back
          up the season after, so there is nothing to remember to do. Cancelling stops it for good.
          Either way you keep the access you have already paid for, until{' '}
          {longDate(access_until)}.
        </p>
      )}

      {/* Said before the button is pressed, not after. Restarting a paused
          subscription is the one action on this card that takes money today. */}
      {seasonOffStarted && (
        <p className="mt-2 text-xs text-slate-500">
          Starting again now charges your card today{price ? ` (${price})` : ''} for a full year
          and moves your renewal to today's date rather than the usual August one.
        </p>
      )}

      <div className="mt-2 space-y-1">
        <ErrorMessage error={pause.error} />
        <ErrorMessage error={resume.error} />
        <ErrorMessage error={cancel.error} />
        <ErrorMessage error={portal.error} />
      </div>
    </Card>
  )
}

// The address itself, and the one action available on it.
//
// There is deliberately no "change your email" field here. Moving an address is
// not an edit — it is a re-verification, and doing it wrong is how an account
// gets taken over by whoever typed the new address. Until that flow exists,
// pointing at a human is the honest answer.
function EmailCard({ user }) {
  const resend = useMutation({ mutationFn: () => api.resendConfirmation() })

  // From the session payload, so this card and the banner in the chrome can
  // never disagree about whether the address is proven.
  const confirmed = user?.email_confirmed === true

  return (
    <Card
      title="Email address"
      subtitle={
        confirmed
          ? 'Confirmed. This is where password resets and renewal notices go.'
          : 'Not confirmed yet.'
      }
    >
      <p className="text-sm text-slate-100">{user?.email_address}</p>

      {!confirmed && (
        <>
          <p className="mt-2 text-xs text-slate-400">
            Until this is confirmed we have no way of knowing the address works — which
            matters for exactly two messages, and they are the two nobody can afford to miss:
            resetting a forgotten password, and the notice we send before a subscription
            renews.
          </p>

          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => resend.mutate()}
              disabled={resend.isPending || resend.isSuccess}
            >
              {resend.isPending
                ? 'Sending…'
                : resend.isSuccess
                  ? 'Link sent'
                  : 'Send a confirmation link'}
            </Button>
            {resend.isSuccess && (
              <span className="text-xs text-slate-400">
                Check your inbox — the link is good for two days.
              </span>
            )}
          </div>

          <div className="mt-2">
            <ErrorMessage error={resend.error} />
          </div>
        </>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Wrong address?{' '}
        <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
          Email us
        </a>{' '}
        and we will move the account by hand.
      </p>
    </Card>
  )
}

function PasswordCard() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setCurrent('')
      setNext('')
    },
  })

  return (
    <Card
      title="Password"
      subtitle="Changing it signs out every other device. This one stays signed in."
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          change.mutate()
        }}
      >
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />

        <ErrorMessage error={change.error} />

        {change.isSuccess && (
          <p className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">
            Password changed. Any other device that was signed in has been signed out.
          </p>
        )}

        <Button type="submit" disabled={change.isPending || !current || !next}>
          {change.isPending ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </Card>
  )
}

// Every linked fantasy account, whatever the provider.
//
// Rendered from the platform_accounts list rather than from a Sleeper-shaped
// assumption, so Yahoo — and ESPN after it — appear here by existing rather
// than by anyone editing this page. Leagues.jsx still reads only the first
// account, which is what makes this the place to see the rest of them.
// A real espn_s2 runs to several hundred URL-encoded characters. A truncated
// paste is by far the likeliest way linking fails, and it surfaces from ESPN as
// a 401 that reads like expired cookies — so the length is shown back while
// typing rather than explained after the fact.
const ESPN_S2_TYPICAL_LENGTH = 250

function ConnectionsCard() {
  const queryClient = useQueryClient()

  const accounts = useQuery({
    queryKey: ['platformAccounts'],
    queryFn: () => api.platformAccounts(),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['platformAccounts'] })
    // Disconnecting takes its imported leagues out of the picker with it.
    queryClient.invalidateQueries({ queryKey: ['leagues'] })
  }

  const unlink = useMutation({
    mutationFn: (id) => api.unlinkPlatformAccount(id),
    onSuccess: invalidate,
  })

  const linked = accounts.data?.platform_accounts || []

  return (
    <Card
      title="Fantasy accounts"
      subtitle="Sleeper and ESPN. Yahoo will appear here when it lands."
    >
      {accounts.isPending && <Spinner label="Loading your connections…" />}
      <ErrorMessage error={accounts.error} />

      {linked.length > 0 && (
        <div className="space-y-2">
          {linked.map((account) => (
            <div
              key={account.id}
              className="flex items-center justify-between gap-3 rounded-md border border-slate-700 px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-100">
                  {account.username || account.external_user_id}
                </div>
                <div className="text-xs text-slate-400">{providerLabel(account.provider)}</div>
              </div>

              <Button
                variant="secondary"
                disabled={unlink.isPending}
                onClick={() => {
                  // A confirm() rather than a styled dialog, because the cost
                  // of a stray click here is a league list that has to be
                  // rebuilt by hand.
                  const ok = window.confirm(
                    `Disconnect ${account.username || 'this account'}?\n\nThe leagues you imported through it are removed from your list. Trades you have already evaluated are not deleted, and you can reconnect at any time.`,
                  )
                  if (ok) unlink.mutate(account.id)
                }}
              >
                {unlink.isPending && unlink.variables === account.id
                  ? 'Disconnecting…'
                  : 'Disconnect'}
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <ErrorMessage error={unlink.error} />
      </div>

      <LinkAccountForm
        label={linked.length > 0 ? 'Connect another account' : 'Connect a fantasy account'}
        onLinked={invalidate}
      />
    </Card>
  )
}

export default function Account() {
  const [params] = useSearchParams()

  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.session(),
    retry: false,
  })

  const entitlement = useQuery({
    queryKey: ['billing', 'entitlement'],
    queryFn: () => api.entitlement(),
    retry: false,
  })

  // Separate from the entitlement on purpose. That answers "may I evaluate a
  // trade"; this answers "what happens to my card next", and the two come apart
  // for exactly the person who most needs an answer — a paused subscriber
  // part-way through their season away, who has no access and is still going to
  // be charged. Fetched unconditionally for that reason.
  const subscription = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.subscription(),
    retry: false,
  })

  const current = entitlement.data?.entitlement
  const sub = subscription.data?.subscription
  const user = session.data?.user

  return (
    <Layout>
      <div className="space-y-4">
        <h1 className="text-lg font-semibold text-slate-100">Account</h1>

        {params.get('checkout') === 'complete' && (
          <p className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">
            Thanks — you're subscribed. If the status below still says otherwise, give it a
            moment and reload; the confirmation from Stripe can take a few seconds to arrive.
          </p>
        )}
        {params.get('checkout') === 'cancelled' && (
          <p className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-300">
            Checkout cancelled. Nothing has been charged.
          </p>
        )}

        {entitlement.isLoading && <Spinner />}
        <ErrorMessage error={entitlement.error} />

        {current && (
          <Card title="Your access">
            <p className="text-sm text-slate-300">{describe(current)}</p>

            {current.source === 'comp' && (
              <p className="mt-2 text-xs text-slate-500">
                There is nothing to manage or cancel.
              </p>
            )}
          </Card>
        )}

        {sub && <SubscriptionCard subscription={sub} />}

        {/* A paused subscriber is not entitled and is not a prospect either —
            they already own this and told us when they want it back. Offering
            them checkout would be offering a second subscription, which the
            server refuses anyway. */}
        {current && current.source === 'free' && sub?.state !== 'paused' && (
          <Paywall entitlement={current} />
        )}

        {user && <EmailCard user={user} />}
        {user && <PasswordCard />}
        {user && <ConnectionsCard />}

        <p className="text-xs text-slate-500">
          <a className="underline" href="/terms">Terms</a>
          {' · '}
          <a className="underline" href="/privacy">Privacy</a>
          {' · '}
          <a className="underline" href="/support">Support</a>
        </p>
      </div>
    </Layout>
  )
}
